import { Tooltip, makeStyles, Button, MenuItem, PopupMenu, PopupMenuItem } from '@paintscout/ui'
import { track } from 'shared/util/analytics'
import type { MuiTheme as Theme, ButtonProps } from '@paintscout/ui'
import {
  ImportJsonDialog,
  useCrmDialogs,
  useClientOptions,
  useUser,
  withDialogStackContext,
  useUserPermissions
} from '@paintscout/ui-business'
import type { WithDialogStackContext } from '@paintscout/ui-business'
import AddIcon from '@material-ui/icons/Add'
import { useSaveEventMutation, type Contact, type Deal, type Task, type Company } from '@paintscout/api'
import { cleanQuote, getObjectLabels } from '@paintscout/util/builder'
import { useFlags } from 'shared/components/common/FlagsProvider'
import type { CalendarEvent, QuoteDocument } from 'paintscout'
import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Feature from 'src/components/common/Feature'
import { ContactDialog, DealDialog, EventDialog, TaskDialog, CompanyDialog } from 'src/components/dialogs'
import CreateQuoteDialog from 'src/components/dialogs/CreateQuoteDialog'
import { goToContact, goToDeal, goToCompany } from 'src/util/routing'
import { getDunningStatusMessage } from 'src/util/dunningMessages'
import capitalize from 'lodash/capitalize'

export interface DropdownNewButtonProps extends Omit<ButtonProps, 'classes'>, WithDialogStackContext {
  classes?: Partial<ButtonProps['classes']>
  disableNewContact?: boolean
  disableImportJSON?: boolean
  forContact?: Contact
  size?: 'small' | 'medium' | 'large'
  onlyFor?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  menuItemDivider: {
    padding: 0,
    height: 0,
    minHeight: 0,
    paddingBottom: theme.spacing(),
    marginBottom: theme.spacing(),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  }
}))

function DropdownNewButton(props: DropdownNewButtonProps) {
  const { disableImportJSON, disableNewContact, forContact, onlyFor, size } = props
  const { isSuperadmin, isSupport, trialExpired, isSubscribed, dunningStatus } = useUser()
  // Payment-failure dunning (#21609) blocks creating new quotes/invoices specifically — not
  // deals/contacts/events/tasks/companies, and not for the people (superadmin/support) helping
  // resolve the account issue. The server enforces this regardless; this is UX only.
  const quoteCreationRestricted =
    !isSuperadmin && !isSupport && (dunningStatus === 'restricted' || dunningStatus === 'locked')
  const dunningTooltip =
    dunningStatus === 'restricted' || dunningStatus === 'locked' ? getDunningStatusMessage(dunningStatus) : ''
  const { openDialog, dismissDialog } = useCrmDialogs()
  const { crm: crmFlag, crmVisible } = useFlags()
  const { options } = useClientOptions()
  const classes = useStyles(props)
  const location = useLocation()
  const history = useHistory()
  const {
    permissionToCreateQuote,
    permissionToCreateInvoice,
    permissionToCreateContact,
    permissionToCreateDeal,
    permissionToCreateEvent,
    permissionToCreateTask,
    permissionToCreateCompany
  } = useUserPermissions()
  const objectLabels = getObjectLabels({ options })
  const forceIntegrationQuotes = !!options?.options?.quotes?.forceIntegrationQuotes
  const [saveEvent] = useSaveEventMutation()

  const addEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    // Do not pass `refetchQueries: ['searchActivities']` here — the search
    // query is Elasticsearch-backed and the new doc may not be indexed yet
    // when the refetch runs, which would wipe the optimistic add performed
    // by CalendarContext's BroadcastChannel listener (the "event flashes
    // then disappears" bug). The 120s poll keeps clients eventually consistent.
    return await saveEvent({
      variables: {
        event,
        eventTemplate
      }
    })
  }

  const permissionsMap = {
    deal: permissionToCreateDeal && !!crmFlag && !!crmVisible,
    quote: permissionToCreateQuote && !forceIntegrationQuotes,
    invoice: permissionToCreateInvoice,
    event: permissionToCreateEvent && !!crmFlag && !!crmVisible,
    task: permissionToCreateTask && !!crmFlag && !!crmVisible,
    contact: !disableNewContact && permissionToCreateContact,
    company: permissionToCreateCompany
  }

  const handleDealDialog = () => {
    track('new_button_clicked', { button_name: 'new_deal' })
    openDialog(DealDialog, {
      pipeline: (location.state as any)?.pipeline,
      onConfirm: (deal: Deal) => {
        goToDeal(deal._id)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleQuoteDialog = () => {
    track('new_button_clicked', { button_name: 'new_quote' })
    openDialog(CreateQuoteDialog, {
      forContact,
      onConfirm: (quote: QuoteDocument) => {
        history.push(`/quotes/new`, { quote })
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleInvoiceDialog = () => {
    track('new_button_clicked', { button_name: 'new_invoice' })
    openDialog(CreateQuoteDialog, {
      invoice: true,
      forContact,
      onConfirm: (quote: QuoteDocument) => {
        history.push(`/invoices/new`, { quote })
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleEventDialog = ({ preventRedirect }: { preventRedirect?: boolean } = {}) => {
    track('new_button_clicked', { button_name: 'new_event' })
    openDialog(EventDialog, {
      isNew: true,
      onConfirm: async (event: CalendarEvent, eventTemplate: string) => {
        const savedEvent = await addEvent(event, eventTemplate)
        if (!preventRedirect) {
          goToDeal(event.deal.id)
        }
        if (typeof BroadcastChannel != 'undefined') {
          const channel = new BroadcastChannel('header-new-button')
          channel.postMessage({
            event: 'new-calendar-event',
            payload: savedEvent?.data?.saveEvent?.activity
          })
        }
        dismissDialog()
      }
    })
  }

  const handleTaskDialog = ({ preventRedirect }: { preventRedirect?: boolean } = {}) => {
    track('new_button_clicked', { button_name: 'new_task' })
    openDialog(TaskDialog, {
      onConfirm: (task: Task) => {
        dismissDialog()
        if (typeof BroadcastChannel !== 'undefined') {
          const channel = new BroadcastChannel('header-new-button')
          channel.postMessage({ event: 'new-task', payload: task })
          channel.close()
        }
        if (!preventRedirect) {
          setTimeout(() => {
            goToDeal(task.dealId)
          }, 500)
        }
      },
      onCancel: dismissDialog
    })
  }

  const handleContactDialog = () => {
    track('new_button_clicked', { button_name: 'new_contact' })
    openDialog(ContactDialog, {
      confirmBeforeCancelWhenDirty: true,
      onConfirm: async (contact: Contact) => {
        goToContact(contact._id)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleCompanyDialog = () => {
    track('new_button_clicked', { button_name: 'new_company' })
    openDialog(CompanyDialog, {
      confirmBeforeCancelWhenDirty: true,
      onConfirm: async (company: Company) => {
        goToCompany(company._id)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleOnlyForClick = () => {
    switch (onlyFor) {
      case 'deal':
        handleDealDialog()
        break
      case 'quote':
        handleQuoteDialog()
        break
      case 'invoice':
        handleInvoiceDialog()
        break
      case 'event':
        handleEventDialog({ preventRedirect: true })
        break
      case 'task':
        handleTaskDialog({ preventRedirect: true })
        break
      case 'contact':
        handleContactDialog()
        break
      case 'company':
        handleCompanyDialog()
        break
      default:
        break
    }
  }

  const getOnlyForLabel = (type: string) => {
    if (type === 'quote') return objectLabels.quote.value
    else return capitalize(type)
  }

  if (Object.values(permissionsMap).every((value) => !value) || (onlyFor && !permissionsMap[onlyFor])) {
    return null
  } else if (onlyFor) {
    const onlyForQuoteRestricted = (onlyFor === 'quote' || onlyFor === 'invoice') && quoteCreationRestricted
    return (
      <Tooltip
        title={trialExpired ? 'Your trial has expired' : onlyForQuoteRestricted ? dunningTooltip : ''}
        placement="left"
      >
        <div>
          <Button
            icon={AddIcon}
            disabled={trialExpired || !isSubscribed || onlyForQuoteRestricted}
            size={size ?? 'medium'}
            onClick={handleOnlyForClick}
            className={`tour-new-${onlyFor}-button`}
          >
            {`New ${getOnlyForLabel(onlyFor)}`}
          </Button>
        </div>
      </Tooltip>
    )
  } else {
    return (
      <Tooltip title={trialExpired ? 'Your trial has expired' : ''} placement="left">
        <div>
          <PopupMenu
            disabled={trialExpired || !isSubscribed}
            component={
              <Button fullWidth icon={AddIcon} disabled={trialExpired || !isSubscribed} size={size ?? 'medium'}>
                New
              </Button>
            }
            id="popup-menu"
          >
            {permissionsMap.deal && <PopupMenuItem onClick={handleDealDialog}>New Deal</PopupMenuItem>}

            {permissionsMap.quote && (
              <Tooltip title={quoteCreationRestricted ? dunningTooltip : ''} placement="left">
                <div>
                  <PopupMenuItem
                    disabled={quoteCreationRestricted}
                    onClick={quoteCreationRestricted ? undefined : handleQuoteDialog}
                  >
                    New {objectLabels.quote.value}
                  </PopupMenuItem>
                </div>
              </Tooltip>
            )}

            {permissionsMap.invoice && (
              <Feature required={{ 'invoices.enabled': true }}>
                <Tooltip title={quoteCreationRestricted ? dunningTooltip : ''} placement="left">
                  <div>
                    <PopupMenuItem
                      disabled={quoteCreationRestricted}
                      onClick={quoteCreationRestricted ? undefined : handleInvoiceDialog}
                    >
                      New Invoice
                    </PopupMenuItem>
                  </div>
                </Tooltip>
              </Feature>
            )}

            {permissionsMap.event && <PopupMenuItem onClick={() => handleEventDialog()}>New Event</PopupMenuItem>}

            {permissionsMap.task && <PopupMenuItem onClick={() => handleTaskDialog()}>New Task</PopupMenuItem>}

            {permissionsMap.contact && <PopupMenuItem onClick={handleContactDialog}>New Contact</PopupMenuItem>}

            {permissionsMap.company && <PopupMenuItem onClick={handleCompanyDialog}>New Company</PopupMenuItem>}

            {!disableImportJSON && (isSuperadmin || isSupport || process.env.DEPLOYMENT_STAGE === 'local') && (
              <MenuItem disabled classes={{ root: classes.menuItemDivider }} />
            )}
            {!disableImportJSON && (isSuperadmin || isSupport || process.env.DEPLOYMENT_STAGE === 'local') && (
              <PopupMenuItem
                onClick={() => {
                  openDialog(ImportJsonDialog, {
                    onImport: (json: JSON) => {
                      const quote = json as unknown as QuoteDocument
                      const cleanedQuote = cleanQuote(quote)

                      history.push(`/quotes/new`, { quote: cleanedQuote })
                      dismissDialog()
                    },
                    onClose: dismissDialog
                  })
                }}
              >
                Import JSON
              </PopupMenuItem>
            )}
          </PopupMenu>
        </div>
      </Tooltip>
    )
  }
}

export default withDialogStackContext(DropdownNewButton)
